/**
 * ================================================================================
 * ConsultationsPage.js - Página Principal de Consultas Médicas (Rol: Doctor)
 * ================================================================================
 *
 * DESCRIPCIÓN:
 * Esta página permite a los médicos gestionar todas las consultas médicas:
 * - Crear nuevas consultas con grabación de audio y transcripción IA
 * - Ver lista de todas las consultas realizadas
 * - Ver detalles y editar consultas existentes
 * - Generar e imprimir recetas médicas
 *
 * SECCIONES PRINCIPALES:
 * 1. Estado y Variables (líneas ~50-145)
 *    - Estados para formulario de nueva consulta
 *    - Estados para diálogo de detalles/edición
 *    - Estados de signos vitales, examen físico, salud mental
 *    - Estados del plan de tratamiento (medicamentos, nutrición, lab, imágenes, interconsultas)
 *
 * 2. Efectos y Fetch de Datos (líneas ~146-188)
 *    - Carga inicial de pacientes y consultas
 *    - Cálculo automático de IMC
 *
 * 3. Funciones de Utilidad (líneas ~189-270)
 *    - getVitalSignColor: Semáforo de signos vitales (verde/rojo)
 *    - handleSubmit: Guardar nueva consulta
 *
 * 4. Manejo de Medicamentos (líneas ~272-480)
 *    - addMedication/removeMedication
 *    - handleAutoFillData: Auto-llenado desde transcripción IA
 *    - handleViewDetails: Cargar datos para ver/editar
 *    - handleUpdateConsultation: Actualizar consulta existente
 *
 * 5. Generación de Recetas (líneas ~482-696)
 *    - parseTreatmentPlan: Parser del plan de tratamiento JSON
 *    - generatePrescriptionContent: Genera HTML de receta
 *    - handlePrintPrescription: Imprime receta
 *    - handleDownloadPrescription: Descarga receta como PDF
 *
 * 6. Renderizado JSX (líneas ~698-1954)
 *    - Header con botón "Nueva Consulta"
 *    - Diálogo de nueva consulta con todas las secciones
 *    - Tabla de lista de consultas
 *    - Diálogo de detalle/edición de consulta
 *
 * CÓMO MODIFICAR:
 * - Para agregar un nuevo campo: añadir estado, input en formulario, y en handleSubmit
 * - Para cambiar signos vitales: modificar VITAL_SIGNS_CONFIG y getVitalSignColor
 * - Para agregar especialidad: añadir al array en sección INTERCONSULTAS
 * - Para modificar receta: editar generatePrescriptionContent
 *
 * DEPENDENCIAS:
 * - ConsultationAudioRecorder: Componente de grabación de audio
 * - api.js: Cliente Axios para llamadas al backend
 * - shadcn/ui: Componentes UI (Dialog, Select, Table, etc.)
 *
 * ENDPOINTS UTILIZADOS:
 * - GET  /api/patients - Lista pacientes del doctor
 * - GET  /api/consultations - Lista consultas del doctor
 * - POST /api/consultations - Crear nueva consulta
 * - PUT  /api/consultations/{id} - Actualizar consulta
 *
 * ================================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ClipboardList, Plus, AlertTriangle, CheckCircle, Trash2, Download, Printer, Pill, Apple, FlaskConical, Image as ImageIcon, Stethoscope, Mic, FileAudio, Play, Pause, Eye, ChevronDown, ChevronUp, Pencil, Share2, Loader2, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import ConsultationAudioRecorder from '../components/ConsultationAudioRecorder';
import FilterBar from '../components/FilterBar';
import PatientSummary from '../components/patient/PatientSummary';
import Cie10Search from '../components/Cie10Search';
import NutritionGuidePicker from '../components/NutritionGuidePicker';
import { useAuth } from '../context/AuthContext';
import { saveDraft, loadDraft, clearDraft, hasContent, describeAge } from '../lib/consultationDraft';
import {
  parseTreatmentPlan,
  parseDiagnosisString,
  generatePrescriptionHTML,
  openPrescriptionWindow,
} from '../lib/prescriptionTemplates';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export default function ConsultationsPageV2() {
  // ===========================================================================
  // SECCIÓN 1: ESTADOS PRINCIPALES
  // ===========================================================================

  /**
   * Lista de consultas del doctor actual
   * Formato: Array de objetos ConsultationResponse del backend
   */
  const [consultations, setConsultations] = useState([]);

  // Filtros del listado. Viven en el cliente: las consultas ya están cargadas,
  // así que filtrar aquí es instantáneo y no añade viajes al servidor.
  const [filters, setFilters] = useState({
    search: '', patientId: 'todos', desde: '', hasta: '',
  });

  /**
   * Lista de pacientes del doctor para el selector
   * Formato: Array de objetos PatientResponse del backend
   */
  const [patients, setPatients] = useState([]);

  /** Estado de carga inicial */
  const [loading, setLoading] = useState(true);

  /** Datos de branding del doctor (logo, colores, datos profesionales) */
  const [doctorBranding, setDoctorBranding] = useState(null);

  /** Control del diálogo de nueva consulta */
  const [dialogOpen, setDialogOpen] = useState(false);

  // ===========================================================================
  // SECCIÓN 2: ESTADOS PARA VER/EDITAR CONSULTA EXISTENTE
  // ===========================================================================

  /** Control del diálogo de detalle/edición */
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  /** Consulta seleccionada para ver/editar */
  const navigate = useNavigate();
  const { user } = useAuth();

  // Borrador recuperable de una consulta que se quedó a medias
  const [recoverableDraft, setRecoverableDraft] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  // Guardando en curso. Sin esto, un segundo clic (o un doble clic, que en un
  // formulario largo es fácil) crea una consulta repetida: en producción había
  // 10 consultas duplicadas de 68, una de ellas repetida seis veces.
  const [isSaving, setIsSaving] = useState(false);

  /** Indica si estamos en modo edición (true) o solo vista (false) */
  const [isEditing, setIsEditing] = useState(false);

  // ===========================================================================
  // ESTADOS PARA LA HOJA DE REFERENCIA
  // ===========================================================================

  /** Control del diálogo de referencia */
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);

  /** Consulta desde la que se está generando la referencia */
  const [referralConsultation, setReferralConsultation] = useState(null);

  /**
   * Datos de la hoja de referencia.
   * No se guardan en la base de datos: se llenan al momento de imprimir
   * y solo alimentan el documento generado.
   */
  const [referralData, setReferralData] = useState({
    establecimiento_destino: '',  // Centro/clínica a donde se deriva
    area_destino: 'Consulta Externa',
    especialidad_destino: '',     // Especialidad a la que se refiere
    motivo: '',                   // Motivo de la referencia
    resumen_historia: '',         // Resumen de la historia clínica
    diagnostico: '',              // Diagnóstico(s) que sustentan la referencia
    fecha_referencia: '',         // Fecha de emisión (YYYY-MM-DD)
    fecha_cita: '',               // Fecha óptima de cita (YYYY-MM-DD)
    telefono: '',                 // Teléfono de contacto del paciente
    comentarios: ''               // Comentarios adicionales
  });

  // ===========================================================================
  // ESTADOS PARA FORMULARIO DE EDICIÓN (Diálogo de Detalles)
  // ===========================================================================

  /**
   * Signos vitales en modo edición
   * Se cargan cuando se abre una consulta para ver/editar
   */
  const [editVitalSigns, setEditVitalSigns] = useState({
    blood_pressure: '',     // Presión arterial (formato: "120/80")
    heart_rate: '',         // Frecuencia cardíaca (lpm)
    temperature: '',        // Temperatura (°C)
    respiratory_rate: '',   // Frecuencia respiratoria (rpm)
    oxygen_saturation: '',  // Saturación de oxígeno (%)
    weight: '',             // Peso (kg)
    height: ''              // Talla (cm)
  });

  /**
   * Examen físico en modo edición
   * Cada sistema tiene: estado ('conservado'|'alterado') y observaciones
   */
  const [editPhysicalExam, setEditPhysicalExam] = useState({
    cabeza: { estado: 'conservado', observaciones: '' },
    cuello: { estado: 'conservado', observaciones: '' },
    cardiovascular: { estado: 'conservado', observaciones: '' },
    respiratorio: { estado: 'conservado', observaciones: '' },
    piel: { estado: 'conservado', observaciones: '' },
    abdomen: { estado: 'conservado', observaciones: '' },
    genitourinario: { estado: 'conservado', observaciones: '' }
  });

  /**
   * Plan de tratamiento en modo edición
   * laboratorio, imagenes e interconsultas se almacenan como strings
   * separados por saltos de línea para edición, y se convierten a arrays al guardar
   */
  const [editTreatment, setEditTreatment] = useState({
    medicamentos: [],           // Array de objetos {nombre, concentracion, forma_farmaceutica, cantidad, dosis, frecuencia, via, duracion, indicaciones}
    nutricion: '',              // Indicaciones nutricionales (texto libre)
    laboratorio: '',            // Exámenes de laboratorio (uno por línea)
    laboratorio_motivo: '',     // Motivo de la solicitud de laboratorio (texto libre)
    imagenes: '',               // Estudios de imagen (uno por línea)
    imagenes_motivo: '',        // Motivo de la solicitud de imágenes (texto libre)
    interconsultas: '',         // Especialidades para interconsulta (una por línea)
    procedimientos: '',         // Procedimientos (uno por línea)
    procedimientos_motivo: ''   // Motivo de la solicitud de procedimientos (texto libre)
  });

  /**
   * Datos del formulario de edición (evaluación clínica y diagnóstico)
   */
  const [editFormData, setEditFormData] = useState({
    clinical_evaluation: '', // Evaluación clínica
    diagnosis: '',           // Texto del diagnóstico
    diagnosisCode: '',       // Código CIE-10
    indications: ''          // Indicaciones adicionales
  });
  
  // ===========================================================================
  // SECCIÓN 3: ESTADOS PARA NUEVA CONSULTA
  // ===========================================================================

  /**
   * Signos vitales para nueva consulta
   * Valores de referencia normales:
   * - Presión: 120/80 mmHg
   * - FC: 60-100 lpm
   * - FR: 12-20 rpm
   * - SatO2: >94%
   * - Temperatura: 36-37.5°C
   */
  const [vitalSigns, setVitalSigns] = useState({
    blood_pressure: '',     // Presión arterial (formato: "120/80")
    heart_rate: '',         // Frecuencia cardíaca (lpm)
    temperature: '',        // Temperatura (°C)
    respiratory_rate: '',   // Frecuencia respiratoria (rpm)
    oxygen_saturation: '',  // Saturación de oxígeno (%)
    weight: '',             // Peso (kg)
    height: ''              // Talla (cm)
  });

  /**
   * IMC (Índice de Masa Corporal) calculado automáticamente
   * Fórmula: peso(kg) / altura(m)²
   * Categorías: Bajo peso (<18.5), Normal (18.5-24.9), Sobrepeso (25-29.9), Obesidad (≥30)
   */
  const [imc, setImc] = useState(null);
  const [imcCategory, setImcCategory] = useState('');

  /**
   * Examen físico por sistemas
   * Cada sistema puede estar "conservado" (normal) o "alterado" (con observaciones)
   */
  const [physicalExam, setPhysicalExam] = useState({
    cabeza: { estado: 'conservado', observaciones: '' },
    cuello: { estado: 'conservado', observaciones: '' },
    cardiovascular: { estado: 'conservado', observaciones: '' },
    respiratorio: { estado: 'conservado', observaciones: '' },
    piel: { estado: 'conservado', observaciones: '' },
    abdomen: { estado: 'conservado', observaciones: '' },
    genitourinario: { estado: 'conservado', observaciones: '' }
  });

  /**
   * Screening de Salud Mental (Whooley/GDS-5)
   * 5 preguntas con respuestas Sí(true)/No(false)/No respondido(null)
   * Sirve para detectar posibles problemas de salud mental
   */
  const [mentalHealth, setMentalHealth] = useState({
    q1: null, // ¿Se ha sentido triste o deprimido?
    q2: null, // ¿Ha perdido interés en actividades?
    q3: null, // ¿Tiene problemas para dormir?
    q4: null, // ¿Se ha sentido ansioso?
    q5: null  // ¿Ha tenido pensamientos negativos?
  });

  /**
   * Plan de Tratamiento dividido en 5 secciones:
   * 1. Medicamentos: Array de objetos con detalles de cada medicamento
   * 2. Nutrición: Indicaciones nutricionales (texto libre)
   * 3. Laboratorio: Array de exámenes de laboratorio solicitados
   * 4. Imágenes: Array de estudios de imagen solicitados
   * 5. Interconsultas: Array de especialidades para derivación
   */
  const [treatment, setTreatment] = useState({
    medicamentos: [],           // [{nombre, concentracion, forma_farmaceutica, cantidad, dosis, frecuencia, via, duracion, indicaciones}]
    nutricion: '',              // Texto libre
    laboratorio: [],            // Array de strings
    laboratorio_motivo: '',     // Motivo de la solicitud de laboratorio (texto libre)
    imagenes: [],               // Array de strings
    imagenes_motivo: '',        // Motivo de la solicitud de imágenes (texto libre)
    interconsultas: [],         // Array de strings
    procedimientos: [],         // Array de strings
    procedimientos_motivo: ''   // Motivo de la solicitud de procedimientos (texto libre)
  });

  /**
   * Datos básicos del formulario de nueva consulta
   */
  const [formData, setFormData] = useState({
    patient_id: '',         // UUID del paciente seleccionado
    reason: '',             // Motivo de consulta
    clinical_evaluation: '', // Evaluación clínica
    tiempo_enfermedad: '',   // Duración de la enfermedad
    relato_enfermedad: '',   // Relato detallado
    funciones_psicologicas: '' // Funciones biológicas
  });

  /** Control de secciones colapsables */
  const [showPatientData, setShowPatientData] = useState(false);
  const [showMentalHealth, setShowMentalHealth] = useState(false);

  /**
   * Audio y Transcripción de la consulta
   * - audioBlob: Blob del archivo de audio grabado
   * - audioUrl: URL del audio para reproducción (puede ser base64)
   * - transcription: Texto transcrito del audio por OpenAI Whisper
   */
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcription, setTranscription] = useState('');

  /**
   * Diagnósticos múltiples con código CIE-10
   * Permite agregar varios diagnósticos, uno marcado como principal
   * Formato: [{diagnosis: string, cie10_code: string, is_primary: boolean}]
   */
  const [diagnoses, setDiagnoses] = useState([
    { diagnosis: '', cie10_code: '', is_primary: true }
  ]);

  /**
   * Estado completo del formulario, para guardarlo y restaurarlo entero.
   * Se deja fuera el audio: son megabytes en base64 y no caben en el
   * almacenamiento del navegador.
   */
  const collectDraft = useCallback(() => ({
    formData, vitalSigns, physicalExam, mentalHealth, treatment, diagnoses,
  }), [formData, vitalSigns, physicalExam, mentalHealth, treatment, diagnoses]);

  // Autoguardado mientras el diálogo está abierto. Con retardo para no escribir
  // en cada tecla: se guarda cuando el médico deja de escribir un momento.
  useEffect(() => {
    if (!dialogOpen) return undefined;

    const draft = collectDraft();
    if (!hasContent(draft)) return undefined;

    const timer = setTimeout(() => {
      saveDraft(user?.id, draft);
      setDraftSavedAt(Date.now());
    }, 1500);

    return () => clearTimeout(timer);
  }, [dialogOpen, collectDraft, user?.id]);

  // Al abrir el diálogo, ofrecer lo que quedó sin guardar la última vez.
  useEffect(() => {
    if (!dialogOpen) {
      setRecoverableDraft(null);
      return;
    }
    const saved = loadDraft(user?.id);
    if (saved && hasContent(saved.draft)) {
      setRecoverableDraft(saved);
    }
  }, [dialogOpen, user?.id]);

  /** Vuelca el borrador en el formulario */
  const restoreDraft = () => {
    const d = recoverableDraft?.draft;
    if (!d) return;
    if (d.formData) setFormData(d.formData);
    if (d.vitalSigns) setVitalSigns(d.vitalSigns);
    if (d.physicalExam) setPhysicalExam(d.physicalExam);
    if (d.mentalHealth) setMentalHealth(d.mentalHealth);
    if (d.treatment) setTreatment(d.treatment);
    if (d.diagnoses) setDiagnoses(d.diagnoses);
    setRecoverableDraft(null);
    toast.success('Consulta recuperada');
  };

  const discardDraft = () => {
    clearDraft(user?.id);
    setRecoverableDraft(null);
  };


  // ===========================================================================
  // SECCIÓN 4: EFECTOS (useEffect)
  // ===========================================================================

  /**
   * Efecto inicial: Cargar datos al montar el componente
   * - Carga la lista de pacientes del doctor
   * - Carga la lista de consultas del doctor
   */
  useEffect(() => {
    fetchPatients();
    fetchConsultations();
    fetchDoctorBranding();
  }, []);

  /**
   * Efecto para calcular IMC automáticamente
   * Se ejecuta cada vez que cambia peso o altura
   * Fórmula: IMC = peso(kg) / altura(m)²
   */
  // El triaje del paciente ya no se pide aquí: PatientSummary trae el último
  // junto con el resto del resumen, en una sola llamada.
  useEffect(() => {
    setShowPatientData(false);
  }, [formData.patient_id]);

  useEffect(() => {
    if (vitalSigns.weight && vitalSigns.height) {
      const weight = parseFloat(vitalSigns.weight);
      const height = parseFloat(vitalSigns.height) / 100; // convertir cm a m
      if (weight > 0 && height > 0) {
        const calculatedImc = (weight / (height * height)).toFixed(2);
        setImc(calculatedImc);

        // Clasificación según OMS
        if (calculatedImc < 18.5) setImcCategory('Bajo peso');
        else if (calculatedImc < 25) setImcCategory('Normal');
        else if (calculatedImc < 30) setImcCategory('Sobrepeso');
        else setImcCategory('Obesidad');
      }
    }
  }, [vitalSigns.weight, vitalSigns.height]);

  // ===========================================================================
  // SECCIÓN 5: FUNCIONES DE CARGA DE DATOS
  // ===========================================================================

  /**
   * Obtiene la lista de pacientes del doctor desde el backend
   * Endpoint: GET /api/patients
   */
  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data);
    } catch (error) {
      toast.error('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtiene la lista de consultas del doctor desde el backend
   * Endpoint: GET /api/consultations
   */
  const fetchConsultations = async () => {
    try {
      const response = await api.get('/consultations');
      setConsultations(response.data);
    } catch (error) {
      console.error('Error fetching consultations:', error);
    }
  };

  /**
   * Obtiene la configuración de branding del doctor (logo, colores, datos)
   * Endpoint: GET /api/doctors/my-branding
   */
  const fetchDoctorBranding = async () => {
    try {
      const response = await api.get('/doctors/my-branding');
      setDoctorBranding(response.data);
    } catch (error) {
      console.error('Error fetching doctor branding:', error);
    }
  };

  // ===========================================================================
  // SECCIÓN 6: FUNCIONES DE UTILIDAD
  // ===========================================================================

  /**
   * Determina el color del signo vital según valores de referencia
   * Sistema de semáforo: Verde = Normal, Rojo = Alerta
   *
   * Valores de alerta:
   * - FR: <12 o >20 rpm
   * - FC: <60 o >100 lpm
   * - SatO2: <94%
   * - PA: Sistólica >140 o Diastólica >90 mmHg
   *
   * @param {string} type - Tipo de signo vital (respiratory_rate, heart_rate, etc.)
   * @param {string|number} value - Valor del signo vital
   * @returns {string} Clase CSS para el color (text-green-600, text-red-600, etc.)
   */
  const getVitalSignColor = (type, value) => {
    const val = parseFloat(value);
    if (!val) return 'text-gray-500';

    switch(type) {
      case 'respiratory_rate':
        // Frecuencia respiratoria: Normal 12-20 rpm
        return (val > 20 || val < 12) ? 'text-red-600 font-bold' : 'text-green-600';
      case 'heart_rate':
        // Frecuencia cardíaca: Normal 60-100 lpm
        return (val > 100 || val < 60) ? 'text-red-600 font-bold' : 'text-green-600';
      case 'oxygen_saturation':
        // Saturación de oxígeno: Normal >94%
        return val < 94 ? 'text-red-600 font-bold' : 'text-green-600';
      case 'blood_pressure':
        // Presión arterial: Normal <140/90 mmHg
        const [systolic, diastolic] = value.split('/').map(v => parseInt(v));
        if (systolic > 140 || diastolic > 90) return 'text-red-600 font-bold';
        return 'text-green-600';
      default:
        return 'text-gray-900';
    }
  };

  // ===========================================================================
  // SECCIÓN 7: MANEJADORES DE FORMULARIO
  // ===========================================================================

  /**
   * Maneja el envío del formulario de nueva consulta
   * Procesa todos los datos y los envía al backend
   *
   * Pasos:
   * 1. Limpia signos vitales (convierte strings vacíos a null, strings a números)
   * 2. Construye string de diagnósticos (primario primero, separados por |)
   * 3. Convierte audio a base64 si existe
   * 4. Envía datos al endpoint POST /api/consultations
   *
   * @param {Event} e - Evento del formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Cinturón además del botón deshabilitado: el submit también se dispara con
    // Enter, y ahí el estado del botón no protege.
    if (isSaving) return;
    setIsSaving(true);

    try {
      // Paso 1: Limpiar y convertir signos vitales
      // Los campos vacíos se envían como null, los numéricos se parsean
      const cleanVitalSigns = {
        blood_pressure: vitalSigns.blood_pressure || null,
        heart_rate: vitalSigns.heart_rate ? parseInt(vitalSigns.heart_rate) : null,
        temperature: vitalSigns.temperature ? parseFloat(vitalSigns.temperature) : null,
        respiratory_rate: vitalSigns.respiratory_rate ? parseInt(vitalSigns.respiratory_rate) : null,
        oxygen_saturation: vitalSigns.oxygen_saturation ? parseInt(vitalSigns.oxygen_saturation) : null,
        weight: vitalSigns.weight ? parseFloat(vitalSigns.weight) : null,
        height: vitalSigns.height ? parseFloat(vitalSigns.height) : null
      };

      // Paso 2: Construir string de diagnósticos
      // Formato: "Diagnóstico Principal (CIE10) | Diagnóstico 2 (CIE10) | ..."
      const sortedDiagnoses = [...diagnoses].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
      const diagnosisString = sortedDiagnoses
        .filter(d => d.diagnosis.trim())
        .map(d => `${d.diagnosis}${d.cie10_code ? ` (${d.cie10_code})` : ''}`)
        .join(' | ');

      // Paso 3: Convertir audio a base64 para almacenamiento
      // El audio se guarda como data URL en la base de datos
      let audioBase64 = null;
      if (audioBlob) {
        const reader = new FileReader();
        audioBase64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(audioBlob);
        });
      }

      // Paso 4: Construir objeto de datos para el backend
      const hasMentalHealthData = Object.values(mentalHealth).some(v => v !== null);
      const consultationData = {
        patient_id: formData.patient_id,
        reason: formData.reason,
        vital_signs: cleanVitalSigns,
        clinical_evaluation: formData.clinical_evaluation,
        diagnosis: diagnosisString,
        treatment_plan: JSON.stringify({ ...treatment, diagnoses: sortedDiagnoses }),
        indications: '',
        audio_url: audioBase64,        // Audio en base64
        transcription: transcription || null,
        tiempo_enfermedad: formData.tiempo_enfermedad || null,
        relato_enfermedad: formData.relato_enfermedad || null,
        funciones_psicologicas: formData.funciones_psicologicas || null,
        mental_health: hasMentalHealthData ? mentalHealth : null,
      };

      // Paso 5: Enviar al backend
      await api.post('/consultations', consultationData);
      clearDraft(user?.id);   // Ya está en la historia: el borrador sobra
      setDraftSavedAt(null);
      toast.success('Consulta registrada exitosamente');
      setDialogOpen(false);
      fetchConsultations(); // Recargar lista de consultas
    } catch (error) {
      // Manejo de errores - puede ser string o array de validaciones
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al registrar consulta';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Agrega un nuevo medicamento vacío al plan de tratamiento
   * El medicamento se agrega con valores por defecto (vía oral)
   */
  const addMedication = () => {
    setTreatment({
      ...treatment,
      medicamentos: [...treatment.medicamentos, {
        nombre: '',
        concentracion: '',
        forma_farmaceutica: 'tableta',
        cantidad: '',
        dosis: '',
        frecuencia: 'c/8h',
        via: 'oral',
        duracion: '',
        indicaciones: ''
      }]
    });
  };

  // ===========================================================================
  // SECCIÓN 8: AUTO-LLENADO DESDE TRANSCRIPCIÓN IA
  // ===========================================================================

  /**
   * Maneja el auto-llenado del formulario desde datos extraídos de la transcripción
   * El componente ConsultationAudioRecorder envía los datos mapeados desde la IA
   *
   * Estructura de mappedData:
   * {
   *   formData: { reason, clinical_evaluation },
   *   vitalSigns: { blood_pressure, heart_rate, ... },
   *   physicalExam: { cabeza: {estado, observaciones}, ... },
   *   mentalHealth: { q1, q2, q3, q4, q5 },
   *   diagnoses: [{ diagnosis, cie10_code, is_primary }],
   *   treatment: { medicamentos, nutricion, laboratorio, imagenes, interconsultas }
   * }
   *
   * @param {Object} mappedData - Datos extraídos de la transcripción por GPT
   */
  const handleAutoFillData = useCallback((mappedData) => {
    if (!mappedData) return;

    // Update form data
    if (mappedData.formData) {
      setFormData(prev => ({
        ...prev,
        reason: mappedData.formData.reason || prev.reason,
        clinical_evaluation: mappedData.formData.clinical_evaluation || prev.clinical_evaluation,
        tiempo_enfermedad: mappedData.formData.tiempo_enfermedad || prev.tiempo_enfermedad,
        relato_enfermedad: mappedData.formData.relato_enfermedad || prev.relato_enfermedad,
        funciones_psicologicas: mappedData.formData.funciones_psicologicas || prev.funciones_psicologicas,
      }));
    }

    // Update vital signs
    if (mappedData.vitalSigns) {
      setVitalSigns(prev => ({
        blood_pressure: mappedData.vitalSigns.blood_pressure || prev.blood_pressure,
        heart_rate: mappedData.vitalSigns.heart_rate || prev.heart_rate,
        temperature: mappedData.vitalSigns.temperature || prev.temperature,
        respiratory_rate: mappedData.vitalSigns.respiratory_rate || prev.respiratory_rate,
        oxygen_saturation: mappedData.vitalSigns.oxygen_saturation || prev.oxygen_saturation,
        weight: mappedData.vitalSigns.weight || prev.weight,
        height: mappedData.vitalSigns.height || prev.height
      }));
    }

    // Update physical exam
    if (mappedData.physicalExam) {
      setPhysicalExam(prev => {
        const newExam = { ...prev };
        Object.keys(mappedData.physicalExam).forEach(sistema => {
          if (mappedData.physicalExam[sistema]) {
            newExam[sistema] = {
              estado: mappedData.physicalExam[sistema].estado || prev[sistema]?.estado || 'conservado',
              observaciones: mappedData.physicalExam[sistema].observaciones || prev[sistema]?.observaciones || ''
            };
          }
        });
        return newExam;
      });
    }

    // Update mental health
    if (mappedData.mentalHealth) {
      setMentalHealth(prev => ({
        q1: mappedData.mentalHealth.q1 !== undefined ? mappedData.mentalHealth.q1 : prev.q1,
        q2: mappedData.mentalHealth.q2 !== undefined ? mappedData.mentalHealth.q2 : prev.q2,
        q3: mappedData.mentalHealth.q3 !== undefined ? mappedData.mentalHealth.q3 : prev.q3,
        q4: mappedData.mentalHealth.q4 !== undefined ? mappedData.mentalHealth.q4 : prev.q4,
        q5: mappedData.mentalHealth.q5 !== undefined ? mappedData.mentalHealth.q5 : prev.q5
      }));
      // Auto-expandir sección de salud mental si se detectaron datos
      const hasAnyMH = Object.values(mappedData.mentalHealth).some(v => v !== null && v !== undefined);
      if (hasAnyMH) {
        setShowMentalHealth(true);
      }
    }

    // Update diagnoses (multiple)
    if (mappedData.diagnoses && mappedData.diagnoses.length > 0) {
      setDiagnoses(mappedData.diagnoses);
    }

    // Update treatment data
    if (mappedData.treatment) {
      setTreatment(prev => ({
        ...prev,
        medicamentos: mappedData.treatment.medicamentos?.length > 0
          ? mappedData.treatment.medicamentos
          : prev.medicamentos,
        nutricion: mappedData.treatment.nutricion || prev.nutricion,
        laboratorio: Array.isArray(mappedData.treatment.laboratorio)
          ? mappedData.treatment.laboratorio
          : prev.laboratorio,
        imagenes: Array.isArray(mappedData.treatment.imagenes)
          ? mappedData.treatment.imagenes
          : prev.imagenes,
        interconsultas: Array.isArray(mappedData.treatment.interconsultas)
          ? mappedData.treatment.interconsultas
          : prev.interconsultas,
        procedimientos: Array.isArray(mappedData.treatment.procedimientos)
          ? mappedData.treatment.procedimientos
          : prev.procedimientos
      }));
    }

    toast.success('Formulario actualizado con datos de la transcripción');
  }, []);

  // ===========================================================================
  // SECCIÓN 9: VER Y EDITAR CONSULTAS EXISTENTES
  // ===========================================================================

  /**
   * Carga los datos de una consulta para ver/editar en el diálogo de detalles
   * Parsea el treatment_plan JSON y separa el diagnóstico del código CIE-10
   *
   * @param {Object} consultation - Objeto de consulta del backend
   */
  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setIsEditing(false);

    // El listado ya no trae el audio ni la transcripción: pesaban megabytes por
    // fila y hacían que la tabla tardase 5-10 segundos en aparecer. Se piden
    // aquí, solo para la consulta que se abre, y se añaden cuando llegan para
    // que el diálogo no espere por ellos.
    if (consultation?.id && consultation.audio_url === undefined) {
      api.get(`/consultations/${consultation.id}`)
        .then(({ data }) => {
          setSelectedConsultation(current => (
            current?.id === data.id
              ? { ...current, audio_url: data.audio_url, transcription: data.transcription }
              : current
          ));
        })
        .catch(() => {
          // Si falla, el detalle sigue siendo útil: solo no habrá audio.
          setSelectedConsultation(current => (
            current?.id === consultation.id ? { ...current, audio_url: null } : current
          ));
        });
    }

    // Load vital signs
    const vs = consultation.vital_signs || {};
    setEditVitalSigns({
      blood_pressure: vs.blood_pressure || '',
      heart_rate: vs.heart_rate || '',
      temperature: vs.temperature || '',
      respiratory_rate: vs.respiratory_rate || '',
      oxygen_saturation: vs.oxygen_saturation || '',
      weight: vs.weight || '',
      height: vs.height || ''
    });

    // Parse and load treatment plan
    let treatmentData = {
      medicamentos: [], nutricion: '',
      laboratorio: '', laboratorio_motivo: '',
      imagenes: '', imagenes_motivo: '',
      interconsultas: '',
      procedimientos: '', procedimientos_motivo: ''
    };
    if (consultation.treatment_plan) {
      try {
        const parsed = JSON.parse(consultation.treatment_plan);
        treatmentData = {
          medicamentos: parsed.medicamentos || [],
          nutricion: parsed.nutricion || '',
          laboratorio: Array.isArray(parsed.laboratorio) ? parsed.laboratorio.join('\n') : (parsed.laboratorio || ''),
          laboratorio_motivo: parsed.laboratorio_motivo || '',
          imagenes: Array.isArray(parsed.imagenes) ? parsed.imagenes.join('\n') : (parsed.imagenes || ''),
          imagenes_motivo: parsed.imagenes_motivo || '',
          interconsultas: Array.isArray(parsed.interconsultas) ? parsed.interconsultas.join('\n') : (parsed.interconsultas || ''),
          procedimientos: Array.isArray(parsed.procedimientos) ? parsed.procedimientos.join('\n') : (parsed.procedimientos || ''),
          procedimientos_motivo: parsed.procedimientos_motivo || ''
        };
      } catch {
        treatmentData.nutricion = consultation.treatment_plan;
      }
    }
    setEditTreatment(treatmentData);

    // Parse diagnosis and code
    let diagnosis = consultation.diagnosis || '';
    let diagnosisCode = '';
    const codeMatch = diagnosis.match(/\(([A-Z]\d+(?:\.\d+)?)\)$/);
    if (codeMatch) {
      diagnosisCode = codeMatch[1];
      diagnosis = diagnosis.replace(/\s*\([A-Z]\d+(?:\.\d+)?\)$/, '').trim();
    }

    setEditFormData({
      clinical_evaluation: consultation.clinical_evaluation || '',
      diagnosis: diagnosis,
      diagnosisCode: diagnosisCode,
      indications: consultation.indications || ''
    });

    setDetailDialogOpen(true);
  };

  /**
   * Abre el detalle de la consulta directamente en modo edición.
   * Se usa desde el botón "Editar" de la tabla, para evitar que el médico
   * tenga que entrar al detalle y luego pulsar "Editar".
   */
  const handleEditConsultation = (consultation) => {
    handleViewDetails(consultation); // carga los datos en los estados de edición
    setIsEditing(true);              // se aplica después del setIsEditing(false) de handleViewDetails
  };

  /**
   * Guarda los cambios de una consulta editada
   * Endpoint: PUT /api/consultations/{id}
   *
   * Convierte los datos del formulario de edición al formato esperado por el backend:
   * - treatment_plan: JSON string con medicamentos, nutrición, lab, imágenes, interconsultas
   * - vital_signs: Objeto con valores numéricos (no strings)
   * - diagnosis: String combinado con código CIE-10
   */
  const handleUpdateConsultation = async () => {
    if (!selectedConsultation) return;

    try {
      // Build diagnosis with code
      const fullDiagnosis = editFormData.diagnosisCode
        ? `${editFormData.diagnosis} (${editFormData.diagnosisCode})`
        : editFormData.diagnosis;

      // La receta imprime los diagnósticos con su CIE-10 desde
      // treatment_plan.diagnoses, así que se reconstruyen a partir de lo que
      // el médico dejó en el formulario. Sin esto, editar una consulta borraba
      // los códigos CIE-10 del plan y la receta salía sin ellos.
      const editedDiagnoses = parseDiagnosisString(fullDiagnosis);

      // Build treatment plan JSON
      const treatmentPlan = JSON.stringify({
        diagnoses: editedDiagnoses,
        medicamentos: editTreatment.medicamentos,
        nutricion: editTreatment.nutricion,
        laboratorio: editTreatment.laboratorio ? editTreatment.laboratorio.split('\n').filter(Boolean) : [],
        laboratorio_motivo: editTreatment.laboratorio_motivo || '',
        imagenes: editTreatment.imagenes ? editTreatment.imagenes.split('\n').filter(Boolean) : [],
        imagenes_motivo: editTreatment.imagenes_motivo || '',
        interconsultas: editTreatment.interconsultas ? editTreatment.interconsultas.split('\n').filter(Boolean) : [],
        procedimientos: editTreatment.procedimientos ? editTreatment.procedimientos.split('\n').filter(Boolean) : [],
        procedimientos_motivo: editTreatment.procedimientos_motivo || ''
      });

      // Build vital signs (convert to proper types)
      const cleanVitalSigns = {
        blood_pressure: editVitalSigns.blood_pressure || null,
        heart_rate: editVitalSigns.heart_rate ? parseInt(editVitalSigns.heart_rate) : null,
        temperature: editVitalSigns.temperature ? parseFloat(editVitalSigns.temperature) : null,
        respiratory_rate: editVitalSigns.respiratory_rate ? parseInt(editVitalSigns.respiratory_rate) : null,
        oxygen_saturation: editVitalSigns.oxygen_saturation ? parseInt(editVitalSigns.oxygen_saturation) : null,
        weight: editVitalSigns.weight ? parseFloat(editVitalSigns.weight) : null,
        height: editVitalSigns.height ? parseFloat(editVitalSigns.height) : null
      };

      await api.put(`/consultations/${selectedConsultation.id}`, {
        clinical_evaluation: editFormData.clinical_evaluation,
        diagnosis: fullDiagnosis,
        treatment_plan: treatmentPlan,
        indications: editFormData.indications,
        vital_signs: cleanVitalSigns
      });
      toast.success('Consulta actualizada exitosamente');
      setIsEditing(false);
      setDetailDialogOpen(false);
      fetchConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar consulta');
    }
  };

  /**
   * Agrega un medicamento vacío en modo edición
   */
  const addEditMedication = () => {
    setEditTreatment({
      ...editTreatment,
      medicamentos: [...editTreatment.medicamentos, {
        nombre: '',
        concentracion: '',
        forma_farmaceutica: 'tableta',
        cantidad: '',
        dosis: '',
        frecuencia: 'c/8h',
        via: 'oral',
        duracion: '',
        indicaciones: ''
      }]
    });
  };

  /**
   * Elimina un medicamento en modo edición
   * @param {number} idx - Índice del medicamento a eliminar
   */
  const removeEditMedication = (idx) => {
    const newMeds = editTreatment.medicamentos.filter((_, i) => i !== idx);
    setEditTreatment({ ...editTreatment, medicamentos: newMeds });
  };

  // ===========================================================================
  // SECCIÓN 10: GENERACIÓN DE RECETAS MÉDICAS
  // ===========================================================================

  /**
   * Genera el HTML imprimible de la consulta.
   *
   * El formato lo decide el médico en Configuración (plantilla clásica vertical
   * o recetario horizontal); la lógica de ambas plantillas vive en
   * `lib/prescriptionTemplates` y la comparte la Historia Clínica del paciente.
   *
   * @param {Object} consultation - Datos de la consulta
   * @returns {string} HTML completo del documento
   */
  const generatePrescriptionContent = (consultation) => {
    return generatePrescriptionHTML({
      consultation,
      patient: patients.find(p => p.id === consultation.patient_id),
      branding: doctorBranding,
    });
  };

  /**
   * Abre la receta en una ventana nueva. Verifica que existan medicamentos
   * antes de generar el documento.
   *
   * @param {Object} consultation - Datos de la consulta
   * @param {boolean} autoPrint - true imprime directo, false muestra el botón "Guardar PDF"
   */
  const openPrescription = (consultation, autoPrint) => {
    const plan = parseTreatmentPlan(consultation.treatment_plan);
    if (!plan?.medicamentos || plan.medicamentos.length === 0) {
      toast.error('Esta consulta no tiene medicamentos prescritos');
      return;
    }

    const opened = openPrescriptionWindow(generatePrescriptionContent(consultation), {
      autoPrint,
      accentColor: doctorBranding?.primary_color || '#2563eb',
    });

    if (!opened) {
      toast.error('El navegador bloqueó la ventana emergente. Permite las ventanas emergentes para este sitio.');
    }
  };

  /** Imprime la receta médica directamente */
  const handlePrintPrescription = (consultation) => openPrescription(consultation, true);

  /** Abre la receta con instrucciones para guardarla como PDF */
  const handleDownloadPrescription = (consultation) => openPrescription(consultation, false);

  /**
   * Verifica si una consulta tiene medicamentos prescritos
   * Se usa para mostrar/ocultar botones de receta
   *
   * @param {Object} consultation - Datos de la consulta
   * @returns {boolean} true si tiene medicamentos, false si no
   */
  const hasMedications = (consultation) => {
    const plan = parseTreatmentPlan(consultation.treatment_plan);
    return plan?.medicamentos && plan.medicamentos.length > 0;
  };

  // ===========================================================================
  // SECCIÓN: HOJA DE REFERENCIA
  // ===========================================================================

  /**
   * Abre el diálogo de referencia con los campos precargados a partir de la consulta.
   * El médico completa el destino y la fecha de cita antes de imprimir.
   *
   * @param {Object} consultation - Datos de la consulta
   */
  const handleOpenReferral = (consultation) => {
    const plan = parseTreatmentPlan(consultation.treatment_plan);
    const patient = patients.find(p => p.id === consultation.patient_id);
    const today = new Date().toISOString().split('T')[0];

    // El resumen se arma con lo que ya está registrado en la consulta,
    // para que el médico no tenga que reescribirlo. Es editable.
    const resumen = [
      consultation.reason ? `Motivo de consulta: ${consultation.reason}` : '',
      consultation.clinical_evaluation ? `Evaluación clínica: ${consultation.clinical_evaluation}` : '',
      plan?.laboratorio?.length ? `Laboratorio: ${plan.laboratorio.join(', ')}` : '',
      plan?.imagenes?.length ? `Imágenes: ${plan.imagenes.join(', ')}` : '',
      plan?.medicamentos?.length ? `Tratamiento: ${plan.medicamentos.map(m => m.nombre).join(', ')}` : ''
    ].filter(Boolean).join('\n');

    setReferralConsultation(consultation);
    setReferralData({
      establecimiento_destino: '',
      area_destino: 'Consulta Externa',
      // Si el médico ya pidió una interconsulta, se usa como especialidad de destino
      especialidad_destino: plan?.interconsultas?.length ? plan.interconsultas[0] : '',
      motivo: '',
      resumen_historia: resumen,
      diagnostico: consultation.diagnosis || '',
      fecha_referencia: today,
      fecha_cita: today,
      telefono: patient?.phone || '',
      comentarios: ''
    });
    setReferralDialogOpen(true);
  };

  /**
   * Genera el HTML de la hoja de referencia (una sola hoja, lista para imprimir).
   * Usa el branding del médico, igual que la receta.
   *
   * @returns {string} HTML completo de la referencia
   */
  const generateReferralContent = () => {
    const consultation = referralConsultation;
    const patient = patients.find(p => p.id === consultation?.patient_id);
    const db = doctorBranding || {};
    const pc = db.primary_color || '#2563eb';
    const doctorName = db.full_name || 'Médico Tratante';
    const doctorSpec = db.specialization || '';
    const doctorLicense = db.license_number || '';

    const patientName = `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim();
    const patientAge = patient?.birth_date
      ? new Date().getFullYear() - new Date(patient.birth_date).getFullYear()
      : null;
    const fmtDate = (d) => d
      ? new Date(`${d}T00:00:00`).toLocaleDateString('es-PE', { day: 'numeric', month: 'long', year: 'numeric' })
      : '-';

    const logoBlock = db.logo_rectangular
      ? `<img src="${db.logo_rectangular}" alt="Logo" />`
      : db.business_name
        ? `<h1>${db.business_name}</h1>`
        : `<div class="placeholder-logo">Logo de empresa</div>`;

    /** Fila etiqueta / valor del bloque de destino */
    const field = (label, value) => `
      <div class="field">
        <label>${label}</label>
        <span>${value || '-'}</span>
      </div>`;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Referencia - ${patientName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #333; }
          .sheet { max-width: 800px; margin: 0 auto; padding: 32px 40px; }

          .header { text-align: center; border-bottom: 3px solid ${pc}; padding-bottom: 14px; margin-bottom: 20px; }
          .header img { max-height: 70px; max-width: 320px; object-fit: contain; margin-bottom: 8px; }
          .header h1 { color: ${pc}; font-size: 24px; margin-bottom: 4px; }
          .header .sheet-title { color: ${pc}; font-size: 20px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
          .placeholder-logo { border: 2px dashed #ccc; padding: 12px 26px; display: inline-block; color: #999; font-size: 13px; border-radius: 8px; margin-bottom: 8px; }

          .info-section { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 18px; padding: 14px 18px; background: #f8fafc; border-radius: 8px; }
          .info-block h3 { color: ${pc}; font-size: 11px; text-transform: uppercase; margin-bottom: 4px; }
          .info-block p { font-size: 13px; }

          .section { margin-bottom: 18px; page-break-inside: avoid; }
          .section h2 { color: ${pc}; font-size: 16px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #e5e7eb; }
          .section-body { padding: 12px 16px; background: #f8fafc; border-radius: 8px; border-left: 4px solid ${pc}; font-size: 14px; white-space: pre-wrap; }

          .fields { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .field { background: #f8fafc; border-radius: 6px; padding: 8px 10px; }
          .field label { display: block; font-size: 10px; color: #666; text-transform: uppercase; margin-bottom: 2px; }
          .field span { font-size: 13px; font-weight: 500; }

          .footer { margin-top: 32px; padding-top: 18px; border-top: 2px solid #e5e7eb; page-break-inside: avoid; }
          .signature-line { width: 200px; border-top: 1px solid #333; margin: 30px auto 10px; }
          .footer-info { text-align: center; }
          .footer-info .name { font-size: 15px; font-weight: bold; }
          .footer-info .detail { font-size: 12px; color: #666; margin-top: 2px; }
          .stamp { margin-top: 14px; font-size: 10px; color: #999; text-align: center; }

          @page { size: A4; margin: 12mm; }
          @media print {
            .sheet { padding: 0; max-width: none; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="header">
            ${logoBlock}
            <p class="sheet-title">Hoja de Referencia</p>
          </div>

          <div class="info-section">
            <div class="info-block">
              <h3>Paciente</h3>
              <p><strong>${patientName || 'N/A'}</strong></p>
              <p>DNI: ${patient?.dni || 'N/A'}</p>
              <p>Edad: ${patientAge !== null ? `${patientAge} años` : 'N/A'}</p>
              <p>Teléfono: ${referralData.telefono || '-'}</p>
            </div>
            <div class="info-block">
              <h3>Médico que refiere</h3>
              <p><strong>Dr(a). ${doctorName}</strong></p>
              <p>${doctorSpec}</p>
              <p>${doctorLicense}</p>
            </div>
            <div class="info-block">
              <h3>Fecha de referencia</h3>
              <p>${fmtDate(referralData.fecha_referencia)}</p>
            </div>
          </div>

          <div class="section">
            <h2>Datos de la Referencia</h2>
            <div class="fields">
              ${field('Establecimiento de destino', referralData.establecimiento_destino)}
              ${field('Área de destino', referralData.area_destino)}
              ${field('Especialidad de destino', referralData.especialidad_destino)}
              ${field('Fecha óptima de cita', fmtDate(referralData.fecha_cita))}
            </div>
          </div>

          ${referralData.motivo ? `
          <div class="section">
            <h2>Motivo de la Referencia</h2>
            <div class="section-body">${referralData.motivo}</div>
          </div>
          ` : ''}

          <div class="section">
            <h2>Diagnóstico</h2>
            <div class="section-body">${referralData.diagnostico || 'No especificado'}</div>
          </div>

          ${referralData.resumen_historia ? `
          <div class="section">
            <h2>Resumen de la Historia Clínica</h2>
            <div class="section-body">${referralData.resumen_historia}</div>
          </div>
          ` : ''}

          ${referralData.comentarios ? `
          <div class="section">
            <h2>Comentarios Adicionales</h2>
            <div class="section-body">${referralData.comentarios}</div>
          </div>
          ` : ''}

          <div class="footer">
            <div class="signature-line"></div>
            <div class="footer-info">
              <div class="name">Dr(a). ${doctorName}</div>
              <div class="detail">${doctorSpec}</div>
              <div class="detail">${doctorLicense}</div>
            </div>
            <p class="stamp">Documento generado digitalmente por ClinIA Perú - ${new Date().toLocaleString('es-PE')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  /**
   * Abre la hoja de referencia en una ventana nueva y lanza la impresión
   */
  const handlePrintReferral = () => {
    if (!referralConsultation) return;
    if (!referralData.especialidad_destino.trim()) {
      toast.error('Indique la especialidad de destino');
      return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateReferralContent());
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
    setReferralDialogOpen(false);
  };

  // ===========================================================================
  // SECCIÓN 11: CONSTANTES PARA SELECTS Y OPCIONES RÁPIDAS
  // ===========================================================================

  const FORMAS_FARMACEUTICAS = [
    'Tableta', 'Cápsula', 'Jarabe', 'Suspensión', 'Ampolla',
    'Crema/Ungüento', 'Gotas', 'Supositorio', 'Inhalador',
    'Solución', 'Gel', 'Óvulo', 'Parche', 'Polvo para suspensión'
  ];

  const FRECUENCIAS_COMUNES = [
    { value: 'stat', label: 'Stat (dosis única)' },
    { value: 'c/4h', label: 'Cada 4 horas' },
    { value: 'c/6h', label: 'Cada 6 horas' },
    { value: 'c/8h', label: 'Cada 8 horas' },
    { value: 'c/12h', label: 'Cada 12 horas' },
    { value: 'c/24h', label: 'Cada 24 horas' },
    { value: 'PRN', label: 'PRN (según necesidad)' }
  ];

  const PROCEDIMIENTOS_COMUNES = [
    'Curación de herida', 'Nebulización', 'Sutura de herida', 'Inyectable (IM/IV/SC)',
    'Vendaje/Inmovilización', 'Electrocardiograma', 'Retiro de puntos', 'Drenaje de absceso',
    'Lavado de oídos', 'Toma de muestra', 'Control de presión arterial', 'Espirometría'
  ];

  // ===========================================================================
  // SECCIÓN 12: RENDERIZADO JSX
  /**
   * Consultas que pasan los filtros activos.
   *
   * La búsqueda mira también el nombre y el DNI del paciente, que es como el
   * médico busca de verdad ("la consulta de Florinda"), no por el motivo.
   */
  const filteredConsultations = consultations.filter((consultation) => {
    if (filters.patientId !== 'todos' && consultation.patient_id !== filters.patientId) {
      return false;
    }

    if (filters.desde || filters.hasta) {
      const date = (consultation.consultation_date || '').slice(0, 10);
      if (filters.desde && date < filters.desde) return false;
      if (filters.hasta && date > filters.hasta) return false;
    }

    const term = filters.search.trim().toLowerCase();
    if (!term) return true;

    const patient = patients.find(p => p.id === consultation.patient_id);
    return [
      patient && `${patient.first_name} ${patient.last_name}`,
      patient?.dni,
      consultation.reason,
      consultation.diagnosis,
    ].some(field => (field || '').toLowerCase().includes(term));
  });

  // ===========================================================================
  // El JSX está organizado en las siguientes partes:
  // 1. Header con título y botón "Nueva Consulta"
  // 2. Diálogo de Nueva Consulta (formulario completo)
  // 3. Tabla de lista de consultas
  // 4. Diálogo de Detalle/Edición de consulta
  // ===========================================================================

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="consultations-title">
            Consultas Médicas
          </h1>
          <p className="text-muted-foreground mt-2">
            Gestiona las consultas médicas de tus pacientes
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="create-consultation-button">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Consulta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid="create-consultation-dialog">
            <DialogHeader>
              <DialogTitle>Registrar Nueva Consulta</DialogTitle>
              <DialogDescription>
                Complete los datos de la consulta médica
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Consulta a medio escribir de una sesión anterior */}
              {recoverableDraft && (
                <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-900 flex-1 min-w-0">
                    Tienes una consulta sin terminar de {describeAge(recoverableDraft.savedAt)}.
                  </p>
                  <Button type="button" size="sm" onClick={restoreDraft}>Recuperar</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={discardDraft}>Descartar</Button>
                </div>
              )}

              {/* Selección de Paciente */}
              <div className="space-y-2">
                <Label htmlFor="patient_id">Paciente *</Label>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <Select value={formData.patient_id} onValueChange={(value) => setFormData({ ...formData, patient_id: value })} required>
                      <SelectTrigger data-testid="patient-select">
                        <SelectValue placeholder="Seleccionar paciente..." />
                      </SelectTrigger>
                      <SelectContent>
                        {patients.map((patient) => (
                          <SelectItem key={patient.id} value={patient.id}>
                            {patient.first_name} {patient.last_name} - DNI: {patient.dni}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Si llega un paciente nuevo, antes había que cerrar el
                      diálogo, ir a Pacientes, registrarlo y empezar de cero. */}
                  <Button type="button" variant="outline" className="shrink-0"
                          onClick={() => navigate('/doctor/patients?nuevo=1')}
                          title="Registrar un paciente nuevo">
                    <UserPlus className="w-4 h-4 mr-2" /> Nuevo paciente
                  </Button>
                </div>
              </div>

              {/* Resumen clínico del paciente.
                  Antes este panel mostraba antecedentes y triaje, y decía "no hay
                  información" aunque el paciente tuviera consultas: no las miraba.
                  Ahora usa el mismo componente que la ficha del paciente. */}
              {formData.patient_id && (
                <Card className="p-4 bg-blue-50/40 border-blue-200">
                  <PatientSummary
                    patientId={formData.patient_id}
                    variant="compact"
                    onOpenConsultation={(c) => {
                      const full = consultations.find(x => x.id === c.id);
                      if (full) {
                        setDialogOpen(false);
                        handleViewDetails(full);
                      }
                    }}
                  />
                </Card>
              )}

              {/* Grabación de Audio y Auto-llenado con IA */}
              {formData.patient_id && (
                <ConsultationAudioRecorder
                  onAutoFillData={handleAutoFillData}
                  onAudioChange={(blob, url) => {
                    setAudioBlob(blob);
                    setAudioUrl(url);
                  }}
                  onTranscriptionChange={(text) => setTranscription(text)}
                  currentFormData={formData}
                  currentTreatment={treatment}
                  disabled={loading}
                />
              )}

              {/* Motivo */}
              <div className="space-y-2">
                <Label htmlFor="reason">Motivo de Consulta *</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  rows={2}
                />
              </div>

              {/* Tiempo de Enfermedad */}
              <div className="space-y-2">
                <Label htmlFor="tiempo_enfermedad">Tiempo de Enfermedad</Label>
                <Input
                  id="tiempo_enfermedad"
                  value={formData.tiempo_enfermedad}
                  onChange={(e) => setFormData({ ...formData, tiempo_enfermedad: e.target.value })}
                  placeholder="Ej: 3 días, 2 semanas, 1 mes"
                />
              </div>

              {/* Relato de Enfermedad */}
              <div className="space-y-2">
                <Label htmlFor="relato_enfermedad">Relato de Enfermedad</Label>
                <Textarea
                  id="relato_enfermedad"
                  value={formData.relato_enfermedad}
                  onChange={(e) => setFormData({ ...formData, relato_enfermedad: e.target.value })}
                  rows={3}
                  placeholder="Descripción detallada del curso de la enfermedad..."
                />
              </div>

              {/* Funciones Biológicas */}
              <div className="space-y-2">
                <Label htmlFor="funciones_psicologicas">Funciones Biológicas</Label>
                <Textarea
                  id="funciones_psicologicas"
                  value={formData.funciones_psicologicas}
                  onChange={(e) => setFormData({ ...formData, funciones_psicologicas: e.target.value })}
                  rows={2}
                  placeholder="Apetito, sed, sueño, orina, deposiciones, sudoración..."
                />
              </div>

              {/* Signos Vitales con Semáforo */}
              <Card className="p-4">
                <h3 className="text-lg font-semibold mb-3">Signos Vitales con Alertas</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Presión Arterial</Label>
                    <Input
                      placeholder="120/80"
                      value={vitalSigns.blood_pressure}
                      onChange={(e) => setVitalSigns({...vitalSigns, blood_pressure: e.target.value})}
                      className={getVitalSignColor('blood_pressure', vitalSigns.blood_pressure)}
                    />
                  </div>
                  <div>
                    <Label>FC (lpm)</Label>
                    <Input
                      type="number"
                      placeholder="70"
                      value={vitalSigns.heart_rate}
                      onChange={(e) => setVitalSigns({...vitalSigns, heart_rate: e.target.value})}
                      className={getVitalSignColor('heart_rate', vitalSigns.heart_rate)}
                    />
                  </div>
                  <div>
                    <Label>FR (rpm)</Label>
                    <Input
                      type="number"
                      placeholder="16"
                      value={vitalSigns.respiratory_rate}
                      onChange={(e) => setVitalSigns({...vitalSigns, respiratory_rate: e.target.value})}
                      className={getVitalSignColor('respiratory_rate', vitalSigns.respiratory_rate)}
                    />
                  </div>
                  <div>
                    <Label>SatO2 (%)</Label>
                    <Input
                      type="number"
                      placeholder="98"
                      value={vitalSigns.oxygen_saturation}
                      onChange={(e) => setVitalSigns({...vitalSigns, oxygen_saturation: e.target.value})}
                      className={getVitalSignColor('oxygen_saturation', vitalSigns.oxygen_saturation)}
                    />
                  </div>
                  <div>
                    <Label>Temperatura (°C)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="36.5"
                      value={vitalSigns.temperature}
                      onChange={(e) => setVitalSigns({...vitalSigns, temperature: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Peso (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="70"
                      value={vitalSigns.weight}
                      onChange={(e) => setVitalSigns({...vitalSigns, weight: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Talla (cm)</Label>
                    <Input
                      type="number"
                      placeholder="170"
                      value={vitalSigns.height}
                      onChange={(e) => setVitalSigns({...vitalSigns, height: e.target.value})}
                    />
                  </div>
                  {imc && (
                    <div className="flex flex-col justify-center">
                      <Label>IMC Calculado</Label>
                      <p className="text-2xl font-bold text-primary">{imc}</p>
                      <p className="text-xs text-muted-foreground">{imcCategory}</p>
                    </div>
                  )}
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                  <p className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Valores en <span className="font-bold text-red-600">ROJO</span> requieren atención
                  </p>
                </div>
              </Card>

              {/* Evaluación Clínica */}
              <div className="space-y-2">
                <Label>Evaluación Clínica</Label>
                <Textarea
                  value={formData.clinical_evaluation}
                  onChange={(e) => setFormData({ ...formData, clinical_evaluation: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Examen Físico con Switches */}
              <Card className="p-4">
                <h3 className="text-lg font-semibold mb-3">Examen Físico por Sistemas</h3>
                <div className="space-y-3">
                  {Object.keys(physicalExam).map((sistema) => (
                    <div key={sistema} className="border-b pb-3">
                      <div className="flex items-center justify-between">
                        <Label className="capitalize">{sistema.replace('_', ' ')}</Label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Conservado</span>
                          <Switch
                            checked={physicalExam[sistema].estado === 'alterado'}
                            onCheckedChange={(checked) => {
                              setPhysicalExam({
                                ...physicalExam,
                                [sistema]: {
                                  ...physicalExam[sistema],
                                  estado: checked ? 'alterado' : 'conservado'
                                }
                              });
                            }}
                          />
                          <span className="text-sm">Alterado</span>
                        </div>
                      </div>
                      {physicalExam[sistema].estado === 'alterado' && (
                        <Textarea
                          placeholder="Describir hallazgos..."
                          value={physicalExam[sistema].observaciones}
                          onChange={(e) => {
                            setPhysicalExam({
                              ...physicalExam,
                              [sistema]: {
                                ...physicalExam[sistema],
                                observaciones: e.target.value
                              }
                            });
                          }}
                          rows={2}
                          className="mt-2"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Diagnósticos con CIE-10 (Múltiples) */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold">Diagnósticos con CIE-10</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDiagnoses([...diagnoses, { diagnosis: '', cie10_code: '', is_primary: false }])}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar Diagnóstico
                  </Button>
                </div>
                <div className="space-y-3">
                  {diagnoses.map((diag, idx) => (
                    <div key={idx} className={`grid grid-cols-12 gap-2 p-3 rounded-lg border ${diag.is_primary ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="col-span-8 space-y-1">
                        <Label className="text-xs">
                          Diagnóstico y CIE-10 {diag.is_primary && <span className="text-blue-600">(Principal)</span>}
                        </Label>
                        {/* Buscar por nombre, por código o por como se dice
                            normalmente ("colon irritable"); al elegir se
                            rellenan diagnóstico y código a la vez. */}
                        <Cie10Search
                          diagnosis={diag.diagnosis}
                          code={diag.cie10_code}
                          onChange={({ diagnosis, cie10_code }) => {
                            const newDiagnoses = [...diagnoses];
                            newDiagnoses[idx] = {
                              ...newDiagnoses[idx],
                              diagnosis,
                              cie10_code: (cie10_code || '').toUpperCase(),
                            };
                            setDiagnoses(newDiagnoses);
                          }}
                        />
                      </div>
                      <div className="col-span-2 flex items-end justify-center pb-1">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input
                            type="radio"
                            name="primaryDiagnosis"
                            checked={diag.is_primary}
                            onChange={() => {
                              const newDiagnoses = diagnoses.map((d, i) => ({
                                ...d,
                                is_primary: i === idx
                              }));
                              setDiagnoses(newDiagnoses);
                            }}
                          />
                          Principal
                        </label>
                      </div>
                      <div className="col-span-2 flex items-end justify-end pb-1">
                        {diagnoses.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            onClick={() => {
                              const newDiagnoses = diagnoses.filter((_, i) => i !== idx);
                              // If we removed the primary, make the first one primary
                              if (diag.is_primary && newDiagnoses.length > 0) {
                                newDiagnoses[0].is_primary = true;
                              }
                              setDiagnoses(newDiagnoses);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Buscar código en: <a href="https://eciemaps.mspsi.es/ecieMaps/browser/index_10_mc.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">eciemaps.mspsi.es</a>
                </p>
              </Card>

              {/* Salud Mental - Colapsable */}
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMentalHealth(!showMentalHealth)}
                  className="w-full justify-between bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-700"
                >
                  <span className="flex items-center gap-2 font-semibold">
                    Screening de Salud Mental (Whooley/GDS-5)
                    {Object.values(mentalHealth).some(v => v !== null) && (
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">Respondido</span>
                    )}
                  </span>
                  {showMentalHealth ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                {showMentalHealth && (
                  <Card className="p-4 bg-purple-50 mt-2 border-purple-200">
                    <div className="space-y-3">
                      {[
                        { key: 'q1', text: '¿Se ha sentido triste, deprimido o desesperanzado?' },
                        { key: 'q2', text: '¿Ha perdido interés o placer en actividades que antes disfrutaba?' },
                        { key: 'q3', text: '¿Tiene problemas para conciliar o mantener el sueño?' },
                        { key: 'q4', text: '¿Se ha sentido nervioso, ansioso o al límite?' },
                        { key: 'q5', text: '¿Ha tenido pensamientos de que estaría mejor muerto?' }
                      ].map((q) => (
                        <div key={q.key} className="flex items-center justify-between p-2 bg-white rounded">
                          <span className="text-sm">{q.text}</span>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={q.key}
                                checked={mentalHealth[q.key] === true}
                                onChange={() => setMentalHealth({...mentalHealth, [q.key]: true})}
                              />
                              Sí
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={q.key}
                                checked={mentalHealth[q.key] === false}
                                onChange={() => setMentalHealth({...mentalHealth, [q.key]: false})}
                              />
                              No
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>

              {/* ==================== PLAN DE TRATAMIENTO - SECCIONES VERTICALES ==================== */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-800 border-b pb-2">Plan de Tratamiento</h3>

                {/* SECCIÓN 1: EXÁMENES DE LABORATORIO */}
                <Card className="p-4 border-l-4 border-l-purple-500">
                  <h4 className="text-lg font-medium text-purple-700 mb-3 flex items-center gap-2">
                    <FlaskConical className="w-5 h-5" />
                    Exámenes de Laboratorio
                  </h4>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      'Hemograma completo', 'Glucosa en ayunas', 'Perfil lipídico', 'Creatinina',
                      'Urea', 'Ácido úrico', 'Perfil hepático', 'Hemoglobina glicosilada',
                      'TSH', 'T4 libre', 'PSA', 'Electrolitos',
                      'Examen de orina', 'Urocultivo', 'PCR', 'VSG'
                    ].map((examen) => {
                      const isSelected = Array.isArray(treatment.laboratorio) && treatment.laboratorio.includes(examen);
                      return (
                        <Button
                          key={examen}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className={`text-xs justify-start ${isSelected ? 'bg-purple-600 hover:bg-purple-700' : 'text-purple-600 border-purple-200 hover:bg-purple-50'}`}
                          onClick={() => {
                            const currentLab = Array.isArray(treatment.laboratorio) ? treatment.laboratorio : [];
                            if (isSelected) {
                              setTreatment({...treatment, laboratorio: currentLab.filter(e => e !== examen)});
                            } else {
                              setTreatment({...treatment, laboratorio: [...currentLab, examen]});
                            }
                          }}
                        >
                          {isSelected ? '✓ ' : ''}{examen}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500">Agregar otro examen</Label>
                      <Input
                        placeholder="Escriba y presione Enter..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            e.preventDefault();
                            const currentLab = Array.isArray(treatment.laboratorio) ? treatment.laboratorio : [];
                            if (!currentLab.includes(e.target.value.trim())) {
                              setTreatment({...treatment, laboratorio: [...currentLab, e.target.value.trim()]});
                            }
                            e.target.value = '';
                          }
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {Array.isArray(treatment.laboratorio) && treatment.laboratorio.length > 0 && (
                    <div className="mt-3 p-2 bg-purple-50 rounded-lg">
                      <Label className="text-xs text-purple-600 font-semibold">Seleccionados ({treatment.laboratorio.length}):</Label>
                      <p className="text-sm text-purple-800 mt-1">{treatment.laboratorio.join(' • ')}</p>
                    </div>
                  )}
                  <div className="mt-3">
                    <Label className="text-xs text-purple-600 font-semibold">Motivo de la solicitud</Label>
                    <Textarea
                      rows={2}
                      placeholder="Ej: descartar anemia, control de perfil lipídico..."
                      value={treatment.laboratorio_motivo}
                      onChange={(e) => setTreatment({ ...treatment, laboratorio_motivo: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </Card>

                {/* SECCIÓN 2: ESTUDIOS DE IMAGEN */}
                <Card className="p-4 border-l-4 border-l-orange-500">
                  <h4 className="text-lg font-medium text-orange-700 mb-3 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Estudios de Imagen
                  </h4>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      'Radiografía de tórax', 'Radiografía de columna', 'Ecografía abdominal', 'Ecografía pélvica',
                      'Ecografía renal', 'Ecografía tiroidea', 'Tomografía cerebral', 'Tomografía abdominal',
                      'Resonancia magnética', 'Mamografía', 'Densitometría ósea', 'Ecocardiograma'
                    ].map((estudio) => {
                      const isSelected = Array.isArray(treatment.imagenes) && treatment.imagenes.includes(estudio);
                      return (
                        <Button
                          key={estudio}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className={`text-xs justify-start ${isSelected ? 'bg-orange-600 hover:bg-orange-700' : 'text-orange-600 border-orange-200 hover:bg-orange-50'}`}
                          onClick={() => {
                            const currentImg = Array.isArray(treatment.imagenes) ? treatment.imagenes : [];
                            if (isSelected) {
                              setTreatment({...treatment, imagenes: currentImg.filter(e => e !== estudio)});
                            } else {
                              setTreatment({...treatment, imagenes: [...currentImg, estudio]});
                            }
                          }}
                        >
                          {isSelected ? '✓ ' : ''}{estudio}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500">Agregar otro estudio</Label>
                      <Input
                        placeholder="Escriba y presione Enter..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            e.preventDefault();
                            const currentImg = Array.isArray(treatment.imagenes) ? treatment.imagenes : [];
                            if (!currentImg.includes(e.target.value.trim())) {
                              setTreatment({...treatment, imagenes: [...currentImg, e.target.value.trim()]});
                            }
                            e.target.value = '';
                          }
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {Array.isArray(treatment.imagenes) && treatment.imagenes.length > 0 && (
                    <div className="mt-3 p-2 bg-orange-50 rounded-lg">
                      <Label className="text-xs text-orange-600 font-semibold">Seleccionados ({treatment.imagenes.length}):</Label>
                      <p className="text-sm text-orange-800 mt-1">{treatment.imagenes.join(' • ')}</p>
                    </div>
                  )}
                  <div className="mt-3">
                    <Label className="text-xs text-orange-600 font-semibold">Motivo de la solicitud</Label>
                    <Textarea
                      rows={2}
                      placeholder="Ej: dolor en hipocondrio derecho, descartar litiasis..."
                      value={treatment.imagenes_motivo}
                      onChange={(e) => setTreatment({ ...treatment, imagenes_motivo: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </Card>

                {/* SECCIÓN 3: PROCEDIMIENTOS */}
                <Card className="p-4 border-l-4 border-l-cyan-500">
                  <h4 className="text-lg font-medium text-cyan-700 mb-3 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5" />
                    Procedimientos
                  </h4>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {PROCEDIMIENTOS_COMUNES.map((proc) => {
                      const isSelected = Array.isArray(treatment.procedimientos) && treatment.procedimientos.includes(proc);
                      return (
                        <Button
                          key={proc}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className={`text-xs justify-start ${isSelected ? 'bg-cyan-600 hover:bg-cyan-700' : 'text-cyan-600 border-cyan-200 hover:bg-cyan-50'}`}
                          onClick={() => {
                            const currentProc = Array.isArray(treatment.procedimientos) ? treatment.procedimientos : [];
                            if (isSelected) {
                              setTreatment({...treatment, procedimientos: currentProc.filter(e => e !== proc)});
                            } else {
                              setTreatment({...treatment, procedimientos: [...currentProc, proc]});
                            }
                          }}
                        >
                          {isSelected ? '✓ ' : ''}{proc}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500">Agregar otro procedimiento</Label>
                      <Input
                        placeholder="Escriba y presione Enter..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            e.preventDefault();
                            const currentProc = Array.isArray(treatment.procedimientos) ? treatment.procedimientos : [];
                            if (!currentProc.includes(e.target.value.trim())) {
                              setTreatment({...treatment, procedimientos: [...currentProc, e.target.value.trim()]});
                            }
                            e.target.value = '';
                          }
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {Array.isArray(treatment.procedimientos) && treatment.procedimientos.length > 0 && (
                    <div className="mt-3 p-2 bg-cyan-50 rounded-lg">
                      <Label className="text-xs text-cyan-600 font-semibold">Seleccionados ({treatment.procedimientos.length}):</Label>
                      <p className="text-sm text-cyan-800 mt-1">{treatment.procedimientos.join(' • ')}</p>
                    </div>
                  )}
                  <div className="mt-3">
                    <Label className="text-xs text-cyan-600 font-semibold">Motivo de la solicitud</Label>
                    <Textarea
                      rows={2}
                      placeholder="Ej: curación de herida operatoria, retiro de puntos..."
                      value={treatment.procedimientos_motivo}
                      onChange={(e) => setTreatment({ ...treatment, procedimientos_motivo: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </Card>

                {/* SECCIÓN 4: TRATAMIENTO FARMACOLÓGICO */}
                <Card className="p-4 border-l-4 border-l-blue-500">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-medium text-blue-700 flex items-center gap-2">
                      <Pill className="w-5 h-5" />
                      Tratamiento Farmacológico
                    </h4>
                    <Button type="button" variant="outline" size="sm" onClick={addMedication} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                      <Plus className="w-4 h-4 mr-1" /> Agregar Medicamento
                    </Button>
                  </div>
                  {treatment.medicamentos.length === 0 ? (
                    <p className="text-gray-400 text-sm italic">No hay medicamentos agregados. Click en "Agregar Medicamento" para añadir uno.</p>
                  ) : (
                    <div className="space-y-3">
                      {treatment.medicamentos.map((med, idx) => (
                        <div key={idx} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-blue-600">Medicamento #{idx + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 px-2"
                              onClick={() => {
                                const newMeds = treatment.medicamentos.filter((_, i) => i !== idx);
                                setTreatment({...treatment, medicamentos: newMeds});
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          {/* Fila 1: Nombre, Concentración, Forma Farmacéutica, Cantidad */}
                          <div className="grid grid-cols-12 gap-2 mb-2">
                            <div className="col-span-4">
                              <Label className="text-xs text-gray-500">Medicamento (DCI)</Label>
                              <Input
                                placeholder="Ej: Paracetamol"
                                value={med.nombre}
                                onChange={(e) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].nombre = e.target.value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Concentración</Label>
                              <Input
                                placeholder="500mg"
                                value={med.concentracion}
                                onChange={(e) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].concentracion = e.target.value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs text-gray-500">Forma Farmacéutica</Label>
                              <Select
                                value={med.forma_farmaceutica}
                                onValueChange={(value) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].forma_farmaceutica = value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {FORMAS_FARMACEUTICAS.map((forma) => (
                                    <SelectItem key={forma} value={forma.toLowerCase()}>{forma}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs text-gray-500">Cantidad a dispensar</Label>
                              <Input
                                placeholder="Ej: 20 tab."
                                value={med.cantidad}
                                onChange={(e) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].cantidad = e.target.value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                                className="mt-1"
                              />
                            </div>
                          </div>
                          {/* Fila 2: Dosis, Frecuencia, Vía, Duración, Indicaciones */}
                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Dosis</Label>
                              <Input
                                placeholder="1 tab."
                                value={med.dosis}
                                onChange={(e) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].dosis = e.target.value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Frecuencia</Label>
                              <Select
                                value={med.frecuencia}
                                onValueChange={(value) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].frecuencia = value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {FRECUENCIAS_COMUNES.map((freq) => (
                                    <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Vía</Label>
                              <Select
                                value={med.via}
                                onValueChange={(value) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].via = value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="oral">Oral</SelectItem>
                                  <SelectItem value="intravenosa">Intravenosa (IV)</SelectItem>
                                  <SelectItem value="intramuscular">Intramuscular (IM)</SelectItem>
                                  <SelectItem value="subcutanea">Subcutánea (SC)</SelectItem>
                                  <SelectItem value="topica">Tópica</SelectItem>
                                  <SelectItem value="inhalatoria">Inhalatoria</SelectItem>
                                  <SelectItem value="sublingual">Sublingual</SelectItem>
                                  <SelectItem value="rectal">Rectal</SelectItem>
                                  <SelectItem value="oftalmica">Oftálmica</SelectItem>
                                  <SelectItem value="otica">Ótica</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Duración</Label>
                              <Input
                                placeholder="7 días"
                                value={med.duracion}
                                onChange={(e) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].duracion = e.target.value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-4">
                              <Label className="text-xs text-gray-500">Indicaciones</Label>
                              <Input
                                placeholder="Ej: Después de comer, con agua"
                                value={med.indicaciones}
                                onChange={(e) => {
                                  const newMeds = [...treatment.medicamentos];
                                  newMeds[idx].indicaciones = e.target.value;
                                  setTreatment({...treatment, medicamentos: newMeds});
                                }}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Indicaciones Nutricionales (subsección dentro de Tratamiento) */}
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h5 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                      <Apple className="w-4 h-4" />
                      Indicaciones Nutricionales
                    </h5>
                    <Textarea
                      placeholder="Ej: Dieta baja en sodio, evitar grasas saturadas, aumentar consumo de frutas y verduras..."
                      value={treatment.nutricion}
                      onChange={(e) => setTreatment({...treatment, nutricion: e.target.value})}
                      rows={2}
                      className="resize-none"
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['Dieta hipocalórica', 'Dieta hiposódica', 'Dieta blanda', 'Aumentar líquidos', 'Evitar grasas', 'Sin azúcar'].map((sugerencia) => (
                        <Button
                          key={sugerencia}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs h-7 text-green-600 border-green-200 hover:bg-green-50"
                          onClick={() => {
                            const currentNutricion = treatment.nutricion;
                            const newNutricion = currentNutricion ? `${currentNutricion}, ${sugerencia}` : sugerencia;
                            setTreatment({...treatment, nutricion: newNutricion});
                          }}
                        >
                          + {sugerencia}
                        </Button>
                      ))}
                    </div>
                    <NutritionGuidePicker
                      patient={patients.find(p => p.id === formData.patient_id)}
                      onInsertSummary={(text) => setTreatment(prev => ({
                        ...prev,
                        nutricion: prev.nutricion ? `${prev.nutricion}\n${text}` : text,
                      }))}
                    />
                  </div>
                </Card>

                {/* SECCIÓN 5: INTERCONSULTAS */}
                <Card className="p-4 border-l-4 border-l-teal-500">
                  <h4 className="text-lg font-medium text-teal-700 mb-3 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5" />
                    Interconsultas
                  </h4>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      'Cardiología', 'Neumología', 'Gastroenterología', 'Endocrinología',
                      'Neurología', 'Nefrología', 'Reumatología', 'Dermatología',
                      'Oftalmología', 'Otorrinolaringología', 'Traumatología', 'Urología',
                      'Ginecología', 'Psiquiatría', 'Nutrición', 'Fisioterapia'
                    ].map((especialidad) => {
                      const isSelected = Array.isArray(treatment.interconsultas) && treatment.interconsultas.includes(especialidad);
                      return (
                        <Button
                          key={especialidad}
                          type="button"
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          className={`text-xs justify-start ${isSelected ? 'bg-teal-600 hover:bg-teal-700' : 'text-teal-600 border-teal-200 hover:bg-teal-50'}`}
                          onClick={() => {
                            const currentInt = Array.isArray(treatment.interconsultas) ? treatment.interconsultas : [];
                            if (isSelected) {
                              setTreatment({...treatment, interconsultas: currentInt.filter(e => e !== especialidad)});
                            } else {
                              setTreatment({...treatment, interconsultas: [...currentInt, especialidad]});
                            }
                          }}
                        >
                          {isSelected ? '✓ ' : ''}{especialidad}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-gray-500">Agregar otra especialidad</Label>
                      <Input
                        placeholder="Escriba y presione Enter..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            e.preventDefault();
                            const currentInt = Array.isArray(treatment.interconsultas) ? treatment.interconsultas : [];
                            if (!currentInt.includes(e.target.value.trim())) {
                              setTreatment({...treatment, interconsultas: [...currentInt, e.target.value.trim()]});
                            }
                            e.target.value = '';
                          }
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {Array.isArray(treatment.interconsultas) && treatment.interconsultas.length > 0 && (
                    <div className="mt-3 p-2 bg-teal-50 rounded-lg">
                      <Label className="text-xs text-teal-600 font-semibold">Seleccionados ({treatment.interconsultas.length}):</Label>
                      <p className="text-sm text-teal-800 mt-1">{treatment.interconsultas.join(' • ')}</p>
                    </div>
                  )}
                </Card>
              </div>
              {/* ==================== FIN PLAN DE TRATAMIENTO ==================== */}

              <div className="flex justify-end items-center gap-2">
                {draftSavedAt && (
                  <span className="text-xs text-slate-400 mr-auto">
                    Borrador guardado {describeAge(draftSavedAt)}
                  </span>
                )}
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Guardar Consulta
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lista de Consultas */}
      <Card className="p-6 space-y-4">
        <FilterBar
          search={filters.search}
          onSearchChange={(value) => setFilters(f => ({ ...f, search: value }))}
          searchPlaceholder="Buscar por paciente, DNI, motivo o diagnóstico..."
          filters={[{
            key: 'patient',
            label: 'Paciente',
            value: filters.patientId,
            options: patients.map(p => ({ value: p.id, label: `${p.first_name} ${p.last_name}` })),
            onChange: (value) => setFilters(f => ({ ...f, patientId: value })),
          }]}
          dateRange={{ desde: filters.desde, hasta: filters.hasta }}
          onDateChange={(field, value) => setFilters(f => ({ ...f, [field]: value }))}
          resultCount={filteredConsultations.length}
          totalCount={consultations.length}
          onClear={() => setFilters({ search: '', patientId: 'todos', desde: '', hasta: '' })}
        />

        {loading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : consultations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No hay consultas registradas</p>
          </div>
        ) : filteredConsultations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">Ninguna consulta coincide con los filtros</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Paciente</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Diagnóstico</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredConsultations.map((consultation) => {
                const patient = patients.find(p => p.id === consultation.patient_id);
                return (
                  <TableRow key={consultation.id}>
                    <TableCell>{new Date(consultation.consultation_date).toLocaleDateString('es-PE')}</TableCell>
                    <TableCell className="font-medium">
                      {patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente no encontrado'}
                    </TableCell>
                    <TableCell>{consultation.reason}</TableCell>
                    <TableCell>{consultation.diagnosis || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewDetails(consultation)}>
                          Ver Detalles
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditConsultation(consultation)}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          title="Editar Consulta"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenReferral(consultation)}
                          className="text-teal-600 border-teal-200 hover:bg-teal-50"
                          title="Generar Hoja de Referencia"
                        >
                          <Share2 className="w-4 h-4 mr-1" />
                          Referencia
                        </Button>
                        {hasMedications(consultation) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPrescription(consultation)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              title="Descargar Receta PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintPrescription(consultation)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              title="Imprimir Receta"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Detail/Edit Dialog - Vertical sections style */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Editar Consulta' : 'Detalles de la Consulta'}</span>
              {!isEditing && (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedConsultation && (
                <span>
                  Fecha: {new Date(selectedConsultation.consultation_date).toLocaleDateString('es-PE', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedConsultation && (
            <div className="space-y-6">
              {/* Información del Paciente */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Paciente</h3>
                {(() => {
                  const patient = patients.find(p => p.id === selectedConsultation.patient_id);
                  return patient ? (
                    <div className="text-blue-800">
                      <p className="font-medium">{patient.first_name} {patient.last_name}</p>
                      <p className="text-sm">DNI: {patient.dni} | Edad: {new Date().getFullYear() - new Date(patient.birth_date).getFullYear()} años</p>
                    </div>
                  ) : <p className="text-blue-600">Paciente no encontrado</p>;
                })()}
              </div>

              {/* Audio y Transcripción de la Consulta */}
              {(selectedConsultation.audio_url || selectedConsultation.transcription) && (
                <Card className="p-4 border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-50 to-blue-50">
                  <h4 className="text-lg font-medium text-indigo-700 mb-3 flex items-center gap-2">
                    <Mic className="w-5 h-5" />
                    Audio y Transcripción de la Consulta
                  </h4>

                  {/* Audio Player */}
                  {selectedConsultation.audio_url && (
                    <div className="mb-3 p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <FileAudio className="w-4 h-4" />
                        <span>Audio de la consulta</span>
                      </div>
                      <audio controls src={selectedConsultation.audio_url} className="w-full h-10" />
                    </div>
                  )}

                  {/* Transcripción */}
                  {selectedConsultation.transcription && (
                    <div className="p-3 bg-white rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-green-800 text-sm">Transcripción:</h5>
                        <span className="text-xs text-gray-400">{selectedConsultation.transcription.length} caracteres</span>
                      </div>
                      {isEditing ? (
                        <Textarea
                          value={editFormData.transcription || selectedConsultation.transcription}
                          onChange={(e) => setEditFormData({...editFormData, transcription: e.target.value})}
                          rows={4}
                          className="text-sm"
                          placeholder="Editar transcripción..."
                        />
                      ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                          {selectedConsultation.transcription}
                        </p>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* Motivo de Consulta */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Motivo de Consulta</Label>
                <p className="p-3 bg-gray-50 rounded-md">{selectedConsultation.reason}</p>
              </div>

              {/* Tiempo y Relato de Enfermedad, Funciones Biológicas */}
              {(selectedConsultation.tiempo_enfermedad || selectedConsultation.relato_enfermedad || selectedConsultation.funciones_psicologicas) && (
                <div className="grid grid-cols-1 gap-3">
                  {selectedConsultation.tiempo_enfermedad && (
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold text-gray-600">Tiempo de Enfermedad</Label>
                      <p className="p-2 bg-gray-50 rounded-md text-sm">{selectedConsultation.tiempo_enfermedad}</p>
                    </div>
                  )}
                  {selectedConsultation.relato_enfermedad && (
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold text-gray-600">Relato de Enfermedad</Label>
                      <p className="p-2 bg-gray-50 rounded-md text-sm whitespace-pre-wrap">{selectedConsultation.relato_enfermedad}</p>
                    </div>
                  )}
                  {selectedConsultation.funciones_psicologicas && (
                    <div className="space-y-1">
                      <Label className="text-sm font-semibold text-gray-600">Funciones Biológicas</Label>
                      <p className="p-2 bg-gray-50 rounded-md text-sm whitespace-pre-wrap">{selectedConsultation.funciones_psicologicas}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Salud Mental en detalle */}
              {selectedConsultation.mental_health && Object.values(selectedConsultation.mental_health).some(v => v !== null) && (
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <h3 className="text-sm font-semibold text-purple-800 mb-2">Screening de Salud Mental</h3>
                  <div className="space-y-1">
                    {[
                      { key: 'q1', text: 'Triste/deprimido' },
                      { key: 'q2', text: 'Pérdida de interés' },
                      { key: 'q3', text: 'Problemas de sueño' },
                      { key: 'q4', text: 'Ansioso/nervioso' },
                      { key: 'q5', text: 'Pensamientos negativos' }
                    ].map((q) => selectedConsultation.mental_health[q.key] !== null && selectedConsultation.mental_health[q.key] !== undefined ? (
                      <div key={q.key} className="flex items-center gap-2 text-sm">
                        <span className={selectedConsultation.mental_health[q.key] ? 'text-red-600 font-medium' : 'text-green-600'}>
                          {selectedConsultation.mental_health[q.key] ? 'Sí' : 'No'}
                        </span>
                        <span className="text-gray-600">- {q.text}</span>
                      </div>
                    ) : null)}
                  </div>
                </Card>
              )}

              {/* Signos Vitales con Alertas */}
              <Card className="p-4">
                <h3 className="text-lg font-semibold mb-3">Signos Vitales con Alertas</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Presión Arterial</Label>
                    <Input
                      placeholder="120/80"
                      value={editVitalSigns.blood_pressure}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, blood_pressure: e.target.value})}
                      className={getVitalSignColor('blood_pressure', editVitalSigns.blood_pressure)}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <Label>FC (lpm)</Label>
                    <Input
                      type="number"
                      placeholder="70"
                      value={editVitalSigns.heart_rate}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, heart_rate: e.target.value})}
                      className={getVitalSignColor('heart_rate', editVitalSigns.heart_rate)}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <Label>FR (rpm)</Label>
                    <Input
                      type="number"
                      placeholder="16"
                      value={editVitalSigns.respiratory_rate}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, respiratory_rate: e.target.value})}
                      className={getVitalSignColor('respiratory_rate', editVitalSigns.respiratory_rate)}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <Label>SatO2 (%)</Label>
                    <Input
                      type="number"
                      placeholder="98"
                      value={editVitalSigns.oxygen_saturation}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, oxygen_saturation: e.target.value})}
                      className={getVitalSignColor('oxygen_saturation', editVitalSigns.oxygen_saturation)}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <Label>Temperatura (°C)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="36.5"
                      value={editVitalSigns.temperature}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, temperature: e.target.value})}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <Label>Peso (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="70"
                      value={editVitalSigns.weight}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, weight: e.target.value})}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <Label>Talla (cm)</Label>
                    <Input
                      type="number"
                      placeholder="170"
                      value={editVitalSigns.height}
                      onChange={(e) => setEditVitalSigns({...editVitalSigns, height: e.target.value})}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                  <p className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Valores en <span className="font-bold text-red-600">ROJO</span> requieren atención
                  </p>
                </div>
              </Card>

              {/* Evaluación Clínica */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Evaluación Clínica</Label>
                <Textarea
                  value={editFormData.clinical_evaluation}
                  onChange={(e) => setEditFormData({...editFormData, clinical_evaluation: e.target.value})}
                  rows={3}
                  disabled={!isEditing}
                  placeholder="Descripción de la evaluación clínica..."
                />
              </div>

              {/* Diagnóstico con CIE-10 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Diagnóstico</Label>
                  <Textarea
                    value={editFormData.diagnosis}
                    onChange={(e) => setEditFormData({...editFormData, diagnosis: e.target.value})}
                    rows={2}
                    disabled={!isEditing}
                    placeholder="Ej: Hipertensión arterial esencial"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Código CIE-10</Label>
                  <Input
                    value={editFormData.diagnosisCode}
                    onChange={(e) => setEditFormData({...editFormData, diagnosisCode: e.target.value})}
                    disabled={!isEditing}
                    placeholder="Ej: I10"
                  />
                  <p className="text-xs text-muted-foreground">Buscar en: eciemaps.mspsi.es</p>
                </div>
              </div>

              {/* ==================== PLAN DE TRATAMIENTO - SECCIONES VERTICALES ==================== */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-800 border-b pb-2">Plan de Tratamiento</h3>

                {/* SECCIÓN 1: LABORATORIO */}
                <Card className="p-4 border-l-4 border-l-purple-500">
                  <h4 className="text-lg font-medium text-purple-700 mb-3 flex items-center gap-2">
                    <FlaskConical className="w-5 h-5" />
                    Exámenes de Laboratorio
                  </h4>
                  <Textarea
                    placeholder="Exámenes de laboratorio solicitados (uno por línea)..."
                    value={editTreatment.laboratorio}
                    onChange={(e) => setEditTreatment({...editTreatment, laboratorio: e.target.value})}
                    rows={3}
                    disabled={!isEditing}
                    className="resize-none"
                  />
                  {editTreatment.laboratorio && !isEditing && (
                    <div className="mt-2 p-2 bg-purple-50 rounded-lg">
                      <ul className="text-sm text-purple-800 list-disc list-inside">
                        {editTreatment.laboratorio.split('\n').filter(Boolean).map((exam, i) => (
                          <li key={i}>{exam}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(isEditing || editTreatment.laboratorio_motivo) && (
                    <div className="mt-3">
                      <Label className="text-xs text-purple-600 font-semibold">Motivo de la solicitud</Label>
                      <Textarea
                        rows={2}
                        placeholder="Ej: descartar anemia, control de perfil lipídico..."
                        value={editTreatment.laboratorio_motivo}
                        onChange={(e) => setEditTreatment({ ...editTreatment, laboratorio_motivo: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1 resize-none"
                      />
                    </div>
                  )}
                </Card>

                {/* SECCIÓN 2: IMÁGENES */}
                <Card className="p-4 border-l-4 border-l-orange-500">
                  <h4 className="text-lg font-medium text-orange-700 mb-3 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Estudios de Imagen
                  </h4>
                  <Textarea
                    placeholder="Estudios de imagen solicitados (uno por línea)..."
                    value={editTreatment.imagenes}
                    onChange={(e) => setEditTreatment({...editTreatment, imagenes: e.target.value})}
                    rows={3}
                    disabled={!isEditing}
                    className="resize-none"
                  />
                  {editTreatment.imagenes && !isEditing && (
                    <div className="mt-2 p-2 bg-orange-50 rounded-lg">
                      <ul className="text-sm text-orange-800 list-disc list-inside">
                        {editTreatment.imagenes.split('\n').filter(Boolean).map((img, i) => (
                          <li key={i}>{img}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(isEditing || editTreatment.imagenes_motivo) && (
                    <div className="mt-3">
                      <Label className="text-xs text-orange-600 font-semibold">Motivo de la solicitud</Label>
                      <Textarea
                        rows={2}
                        placeholder="Ej: dolor en hipocondrio derecho, descartar litiasis..."
                        value={editTreatment.imagenes_motivo}
                        onChange={(e) => setEditTreatment({ ...editTreatment, imagenes_motivo: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1 resize-none"
                      />
                    </div>
                  )}
                </Card>

                {/* SECCIÓN 3: PROCEDIMIENTOS */}
                <Card className="p-4 border-l-4 border-l-cyan-500">
                  <h4 className="text-lg font-medium text-cyan-700 mb-3 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5" />
                    Procedimientos
                  </h4>
                  <Textarea
                    placeholder="Procedimientos realizados (uno por línea)..."
                    value={editTreatment.procedimientos}
                    onChange={(e) => setEditTreatment({...editTreatment, procedimientos: e.target.value})}
                    rows={3}
                    disabled={!isEditing}
                    className="resize-none"
                  />
                  {editTreatment.procedimientos && !isEditing && (
                    <div className="mt-2 p-2 bg-cyan-50 rounded-lg">
                      <ul className="text-sm text-cyan-800 list-disc list-inside">
                        {editTreatment.procedimientos.split('\n').filter(Boolean).map((proc, i) => (
                          <li key={i}>{proc}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(isEditing || editTreatment.procedimientos_motivo) && (
                    <div className="mt-3">
                      <Label className="text-xs text-cyan-600 font-semibold">Motivo de la solicitud</Label>
                      <Textarea
                        rows={2}
                        placeholder="Ej: curación de herida operatoria, retiro de puntos..."
                        value={editTreatment.procedimientos_motivo}
                        onChange={(e) => setEditTreatment({ ...editTreatment, procedimientos_motivo: e.target.value })}
                        disabled={!isEditing}
                        className="mt-1 resize-none"
                      />
                    </div>
                  )}
                </Card>

                {/* SECCIÓN 4: TRATAMIENTO FARMACOLÓGICO */}
                <Card className="p-4 border-l-4 border-l-blue-500">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-medium text-blue-700 flex items-center gap-2">
                      <Pill className="w-5 h-5" />
                      Tratamiento Farmacológico
                    </h4>
                    {isEditing && (
                      <Button type="button" variant="outline" size="sm" onClick={addEditMedication} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                        <Plus className="w-4 h-4 mr-1" /> Agregar
                      </Button>
                    )}
                  </div>
                  {editTreatment.medicamentos.length === 0 ? (
                    <p className="text-gray-400 text-sm italic">No hay medicamentos registrados</p>
                  ) : (
                    <div className="space-y-3">
                      {editTreatment.medicamentos.map((med, idx) => (
                        <div key={idx} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-blue-600">Medicamento #{idx + 1}</span>
                            {isEditing && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 px-2"
                                onClick={() => removeEditMedication(idx)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          {/* Fila 1: Nombre, Concentración, Forma Farmacéutica, Cantidad */}
                          <div className="grid grid-cols-12 gap-2 mb-2">
                            <div className="col-span-4">
                              <Label className="text-xs text-gray-500">Medicamento (DCI)</Label>
                              <Input
                                placeholder="Ej: Paracetamol"
                                value={med.nombre}
                                onChange={(e) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].nombre = e.target.value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Concentración</Label>
                              <Input
                                placeholder="500mg"
                                value={med.concentracion || ''}
                                onChange={(e) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].concentracion = e.target.value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs text-gray-500">Forma Farmacéutica</Label>
                              <Select
                                value={med.forma_farmaceutica || 'tableta'}
                                onValueChange={(value) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].forma_farmaceutica = value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {FORMAS_FARMACEUTICAS.map((forma) => (
                                    <SelectItem key={forma} value={forma.toLowerCase()}>{forma}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs text-gray-500">Cantidad</Label>
                              <Input
                                placeholder="Ej: 20 tab."
                                value={med.cantidad || ''}
                                onChange={(e) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].cantidad = e.target.value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                                className="mt-1"
                              />
                            </div>
                          </div>
                          {/* Fila 2: Dosis, Frecuencia, Vía, Duración, Indicaciones */}
                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Dosis</Label>
                              <Input
                                placeholder="1 tab."
                                value={med.dosis}
                                onChange={(e) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].dosis = e.target.value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Frecuencia</Label>
                              <Select
                                value={med.frecuencia || 'c/8h'}
                                onValueChange={(value) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].frecuencia = value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {FRECUENCIAS_COMUNES.map((freq) => (
                                    <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Vía</Label>
                              <Select
                                value={med.via || 'oral'}
                                onValueChange={(value) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].via = value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="oral">Oral</SelectItem>
                                  <SelectItem value="intravenosa">IV</SelectItem>
                                  <SelectItem value="intramuscular">IM</SelectItem>
                                  <SelectItem value="subcutanea">SC</SelectItem>
                                  <SelectItem value="topica">Tópica</SelectItem>
                                  <SelectItem value="inhalatoria">Inhalatoria</SelectItem>
                                  <SelectItem value="sublingual">Sublingual</SelectItem>
                                  <SelectItem value="rectal">Rectal</SelectItem>
                                  <SelectItem value="oftalmica">Oftálmica</SelectItem>
                                  <SelectItem value="otica">Ótica</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs text-gray-500">Duración</Label>
                              <Input
                                placeholder="7 días"
                                value={med.duracion}
                                onChange={(e) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].duracion = e.target.value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-4">
                              <Label className="text-xs text-gray-500">Indicaciones</Label>
                              <Input
                                placeholder="Ej: Después de comer"
                                value={med.indicaciones || ''}
                                onChange={(e) => {
                                  const newMeds = [...editTreatment.medicamentos];
                                  newMeds[idx].indicaciones = e.target.value;
                                  setEditTreatment({...editTreatment, medicamentos: newMeds});
                                }}
                                disabled={!isEditing}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Indicaciones Nutricionales (subsección) */}
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h5 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                      <Apple className="w-4 h-4" />
                      Indicaciones Nutricionales
                    </h5>
                    <Textarea
                      placeholder="Indicaciones nutricionales..."
                      value={editTreatment.nutricion}
                      onChange={(e) => setEditTreatment({...editTreatment, nutricion: e.target.value})}
                      rows={2}
                      disabled={!isEditing}
                      className="resize-none"
                    />
                    {editTreatment.nutricion && !isEditing && (
                      <div className="mt-2 p-2 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800">{editTreatment.nutricion}</p>
                      </div>
                    )}
                    <NutritionGuidePicker
                      patient={patients.find(p => p.id === selectedConsultation?.patient_id)}
                      consultationId={selectedConsultation?.id}
                      disabled={!isEditing}
                      onInsertSummary={(text) => setEditTreatment(prev => ({
                        ...prev,
                        nutricion: prev.nutricion ? `${prev.nutricion}\n${text}` : text,
                      }))}
                    />
                  </div>
                </Card>

                {/* SECCIÓN 5: INTERCONSULTAS */}
                <Card className="p-4 border-l-4 border-l-teal-500">
                  <h4 className="text-lg font-medium text-teal-700 mb-3 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5" />
                    Interconsultas
                  </h4>
                  <Textarea
                    placeholder="Interconsultas solicitadas (una por línea)..."
                    value={editTreatment.interconsultas}
                    onChange={(e) => setEditTreatment({...editTreatment, interconsultas: e.target.value})}
                    rows={3}
                    disabled={!isEditing}
                    className="resize-none"
                  />
                  {editTreatment.interconsultas && !isEditing && (
                    <div className="mt-2 p-2 bg-teal-50 rounded-lg">
                      <ul className="text-sm text-teal-800 list-disc list-inside">
                        {editTreatment.interconsultas.split('\n').filter(Boolean).map((int, i) => (
                          <li key={i}>{int}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              </div>
              {/* ==================== FIN PLAN DE TRATAMIENTO ==================== */}

              {/* Indicaciones Adicionales */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Indicaciones Adicionales</Label>
                <Textarea
                  value={editFormData.indications}
                  onChange={(e) => setEditFormData({...editFormData, indications: e.target.value})}
                  rows={3}
                  disabled={!isEditing}
                  placeholder="Indicaciones adicionales para el paciente..."
                />
              </div>

              {/* Botones de acción */}
              <div className="flex justify-between items-center pt-4 border-t">
                {/* Botones de Receta */}
                <div className="flex gap-2">
                  {selectedConsultation && hasMedications(selectedConsultation) && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleDownloadPrescription(selectedConsultation)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Descargar Receta
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handlePrintPrescription(selectedConsultation)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimir Receta
                      </Button>
                    </>
                  )}
                </div>
                {/* Botones de Edición */}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setIsEditing(false); setDetailDialogOpen(false); }}>
                    {isEditing ? 'Cancelar' : 'Cerrar'}
                  </Button>
                  {isEditing && (
                    <Button onClick={handleUpdateConsultation} className="bg-blue-600 hover:bg-blue-700">
                      Guardar Cambios
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== DIÁLOGO: HOJA DE REFERENCIA ==================== */}
      <Dialog open={referralDialogOpen} onOpenChange={setReferralDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-teal-600" />
              Hoja de Referencia
            </DialogTitle>
            <DialogDescription>
              Complete los datos de destino. La referencia se imprime en una sola hoja y no se
              guarda en la historia clínica.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Establecimiento de destino</Label>
                <Input
                  placeholder="Ej: Hospital Nacional Dos de Mayo"
                  value={referralData.establecimiento_destino}
                  onChange={(e) => setReferralData({ ...referralData, establecimiento_destino: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Área de destino</Label>
                <Input
                  placeholder="Ej: Consulta Externa"
                  value={referralData.area_destino}
                  onChange={(e) => setReferralData({ ...referralData, area_destino: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Especialidad de destino *</Label>
                <Input
                  placeholder="Ej: Gastroenterología"
                  value={referralData.especialidad_destino}
                  onChange={(e) => setReferralData({ ...referralData, especialidad_destino: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Teléfono de contacto</Label>
                <Input
                  placeholder="Ej: 941853195"
                  value={referralData.telefono}
                  onChange={(e) => setReferralData({ ...referralData, telefono: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Fecha de referencia</Label>
                <Input
                  type="date"
                  value={referralData.fecha_referencia}
                  onChange={(e) => setReferralData({ ...referralData, fecha_referencia: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Fecha óptima de cita</Label>
                <Input
                  type="date"
                  value={referralData.fecha_cita}
                  onChange={(e) => setReferralData({ ...referralData, fecha_cita: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Motivo de la referencia</Label>
              <Textarea
                rows={2}
                placeholder="Ej: requiere evaluación por especialista y endoscopía..."
                value={referralData.motivo}
                onChange={(e) => setReferralData({ ...referralData, motivo: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Diagnóstico</Label>
              <Textarea
                rows={2}
                value={referralData.diagnostico}
                onChange={(e) => setReferralData({ ...referralData, diagnostico: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Resumen de la historia clínica</Label>
              <Textarea
                rows={5}
                value={referralData.resumen_historia}
                onChange={(e) => setReferralData({ ...referralData, resumen_historia: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Precargado con los datos de la consulta. Puede editarlo antes de imprimir.
              </p>
            </div>

            <div>
              <Label>Comentarios adicionales</Label>
              <Textarea
                rows={2}
                value={referralData.comentarios}
                onChange={(e) => setReferralData({ ...referralData, comentarios: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setReferralDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handlePrintReferral} className="bg-teal-600 hover:bg-teal-700">
                <Printer className="w-4 h-4 mr-2" />
                Imprimir Referencia
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
